import { initConfig, setConfigByKey } from '@bigo/logger';
import getNewDeviceId from '@/libs/deviceId';
import nativeApi from '@bigo/nativeapi-bigo';
import { getUaInfo, isBigolive, isIOS } from '.';
import { STORAGE_KEY } from '@/constants/storage';

// 首屏上报，性能指标监控代码，勿删
// 首屏上报时机文档：https://bgtest-fed.bigolive.tv/live/pages/bigolive/assets-load-monitor-demo/index.html
export const firstScreenReport = () => {
  window.assetsLoadMonitor?.sendCustomKeyReport({
    // 自定义打点类型， type参数不能是page_view_time 和 white_screen_time， 因为会和公共库pv和白屏打点冲突
    type: 'first_screen_time',
    // 扩展信息， 业务按需传
    extra: {
      msg: '首屏时间',
    },
  });
};

// 如果需要覆盖，可以外部调用setConfigByKey(hooks, {...})
const defaultLoggerHooks = {
  xhr: true,
  nativeapi: true,
  history: true,
} as Record<string, boolean>;

export const getDeviceId = async () => {
  const lastDeviceId = localStorage.getItem(STORAGE_KEY.DEVICE_ID);
  if (lastDeviceId) {
    return lastDeviceId;
  }
  if (isBigolive) {
    const res = await nativeApi.DeviceInfo();
    if (res.deviceid) {
      localStorage.setItem(STORAGE_KEY.DEVICE_ID, res.deviceid);
      return res.deviceid;
    }
  }

  const deviceId = await getNewDeviceId();
  localStorage.setItem(STORAGE_KEY.DEVICE_ID, deviceId);

  return deviceId;
};

export const initDeviceId = async () => {
  const deviceId = await getDeviceId();
  localStorage.setItem(STORAGE_KEY.DEVICE_ID, deviceId);
};

const getGlobalReportConfig = async () => {
  const uaInfo = getUaInfo();
  const appName = 'agent-oss';
  const source = `${appName}-${import.meta.env.VITE_BUILD_ENV}`;
  const deviceId = await getDeviceId();
  return {
    device_id: uaInfo.deviceid || deviceId || '',
    source,
    uid: uaInfo.userid,
    // 如果要使用track，act_id需要加上，值为埋点上报的act_id
    act_id: 66965,
  };
};

export const loggerInit = async () => {
  const globalConfig = await getGlobalReportConfig();

  initConfig({
    hooks: defaultLoggerHooks,
    isDisableRequestid: true,
    globalReportConfig: globalConfig,
  });
};

export const updateLoggerConfig = async (options = {}) => {
  const globalConfig = await getGlobalReportConfig();
  setConfigByKey('globalReportConfig', {
    ...globalConfig,
    ...options,
  });
};

export const initScroll = () => {
  if (!isBigolive || !isIOS) {
    return;
  }
  // 递归查找可以滚动的祖先元素
  const findScrollableAncestor = (element: HTMLElement | null): HTMLElement | null => {
    if (!element) {
      return null;
    }

    // 检查元素是否可以滚动
    if (element.scrollHeight - element.clientHeight > 10) {
      return element;
    }
    return findScrollableAncestor(element.parentElement);
  };

  document.addEventListener(
    'touchstart',
    (e) => {
      const target = e.touches[0].target as HTMLElement;
      if (!target?.className?.toString().includes('arco-table')) {
        return;
      }

      // 寻找可以滚动的祖先元素
      const scrollableElement = findScrollableAncestor(target);

      if (scrollableElement) {
        const startX = e.touches[0].clientX;
        const startY = e.touches[0].clientY;
        const startScrollTop = scrollableElement.scrollTop;

        const onMove = (e: TouchEvent) => {
          if (e.touches.length === 1) {
            const deltaX = Math.abs(e.touches[0].clientX - startX);
            const deltaY = Math.abs(e.touches[0].clientY - startY);

            if (deltaX > deltaY) {
              // 横向滚动较大，忽略纵向滚动
              return;
            }

            const scrollDeltaY = e.touches[0].clientY - startY;

            // 计算滚动位置
            const newScrollTop = startScrollTop - scrollDeltaY;

            // 防止滚动超出边界
            if (
              newScrollTop >= 0 &&
              newScrollTop <= scrollableElement.scrollHeight - scrollableElement.clientHeight
            ) {
              // 根据触摸移动的距离滚动容器
              scrollableElement.scrollTo({
                top: newScrollTop,
              });
            }
          }
        };

        document.addEventListener('touchmove', onMove, false);

        const onTouchEnd = () => {
          // 清除状态
          document.removeEventListener('touchmove', onMove, false);
          document.removeEventListener('touchend', onTouchEnd, false);
        };

        document.addEventListener('touchend', onTouchEnd, false);
      }
    },
    false,
  );
};
